<script lang="ts" setup>
import { ButtonVariant } from './helpers'
import type { AbstractButtonProps } from './types'

defineOptions({
  inheritAttrs: false,
})

withDefaults(defineProps<AbstractButtonProps>(), {
  as: 'button',
  variant: ButtonVariant.Primary,
  disabled: false,
})
</script>

<template>
  <component
    :is="as"
    draggable="false"
    v-bind="$attrs"
    class="glp-button"
    :class="{
      'glp-button--primary': variant === ButtonVariant.Primary,
      'glp-button--secondary': variant === ButtonVariant.Secondary,
      'glp-button--tertiary': variant === ButtonVariant.Tertiary,
      'glp-button--disabled': disabled,
    }"
    :disabled="as === 'button' ? disabled : undefined"
    :tabindex="disabled ? -1 : $attrs.tabindex"
  >
    <slot></slot>
  </component>
</template>

<style>
.glp-button {
  user-select: none;
  display: inline-flex;
  color: var(--glp-button-foreground);
  background: var(--glp-button-background);
  border: var(--glp-button-border-width, 1px) solid
    var(--glp-button-border-color);
  border-radius: var(--glp-button-border-radius);
  cursor: pointer;
}

/** States */
.glp-button {
  --glp-button-foreground: var(--glp-button-foreground-default);
  --glp-button-background: var(--glp-button-background-default);
  --glp-button-border-color: var(--glp-button-border-color-default);
}
.glp-button:hover {
  --glp-button-foreground: var(--glp-button-foreground-hover);
  --glp-button-background: var(--glp-button-background-hover);
  --glp-button-border-color: var(--glp-button-border-color-hover);
}
.glp-button:active {
  --glp-button-foreground: var(--glp-button-foreground-pressed);
  --glp-button-background: var(--glp-button-background-pressed);
  --glp-button-border-color: var(--glp-button-border-color-pressed);
}
.glp-button:disabled,
.glp-button--disabled {
  opacity: 0.5;
  pointer-events: none;
}
.glp-button:focus-visible {
  outline: 3px solid var(--glp-general-color-focus);
  outline-offset: 3px;
}

/** Variants */
.glp-button--primary {
  --glp-button-border-color-default: var(
    --glp-button-primary-default-color-border
  );
  --glp-button-background-default: var(--glp-button-primary-default-background);
  --glp-button-foreground-default: var(--glp-button-primary-default-foreground);
  --glp-button-border-color-hover: var(--glp-button-primary-hover-color-border);
  --glp-button-background-hover: var(--glp-button-primary-hover-background);
  --glp-button-foreground-hover: var(--glp-button-primary-hover-foreground);
  --glp-button-border-color-pressed: var(
    --glp-button-primary-pressed-color-border
  );
  --glp-button-background-pressed: var(--glp-button-primary-pressed-background);
  --glp-button-foreground-pressed: var(--glp-button-primary-pressed-foreground);
}

.glp-button--secondary {
  --glp-button-border-color-default: var(
    --glp-button-secondary-default-color-border
  );
  --glp-button-background-default: var(
    --glp-button-secondary-default-background
  );
  --glp-button-foreground-default: var(
    --glp-button-secondary-default-foreground
  );
  --glp-button-border-color-hover: var(
    --glp-button-secondary-hover-color-border
  );
  --glp-button-background-hover: var(--glp-button-secondary-hover-background);
  --glp-button-foreground-hover: var(--glp-button-secondary-hover-foreground);
  --glp-button-border-color-pressed: var(
    --glp-button-secondary-pressed-color-border
  );
  --glp-button-background-pressed: var(
    --glp-button-secondary-pressed-background
  );
  --glp-button-foreground-pressed: var(
    --glp-button-secondary-pressed-foreground
  );
}

.glp-button--tertiary {
  --glp-button-border-color-default: var(
    --glp-button-tertiary-default-color-border
  );
  --glp-button-background-default: var(
    --glp-button-tertiary-default-background
  );
  --glp-button-foreground-default: var(
    --glp-button-tertiary-default-foreground
  );
  --glp-button-border-color-hover: var(
    --glp-button-tertiary-hover-color-border
  );
  --glp-button-background-hover: var(--glp-button-tertiary-hover-background);
  --glp-button-foreground-hover: var(--glp-button-tertiary-hover-foreground);
  --glp-button-border-color-pressed: var(
    --glp-button-tertiary-pressed-color-border
  );
  --glp-button-background-pressed: var(
    --glp-button-tertiary-pressed-background
  );
  --glp-button-foreground-pressed: var(
    --glp-button-tertiary-pressed-foreground
  );
}
</style>
